import React, { useCallback, useMemo, useRef } from 'react'
import { showAlert } from '../scripts/notifications'
import { useDispatch } from '../store/hooks'
import { useCurrentApplication } from '../store/hooks/uiHooks'
import { useCurrentConfiguration } from '../store/hooks/configurationHooks'
import { useFinalForm } from '../store/hooks/formHooks'
import { useTranslation } from '../store/hooks/dataHooks'
import Form, { FormRef } from '../components/forms/Form'
import Input from '../components/forms/Input'
import Textarea from '../components/forms/Textarea'
import NoticeRequired from '../components/forms/NoticeRequired'
import Submit from '../components/forms/Submit'
import Select from '../components/forms/Select'

const FinalForm: React.FC = () => {
  const dispatch = useDispatch()
  const t = useTranslation()
  const { id: applicationId = '' } = useCurrentApplication() ?? {}
  const configuration = useCurrentConfiguration()

  const form = useRef<FormRef>(null)
  const { loading, submit } = useFinalForm()

  const hideFinal = useCallback(() => {
    dispatch({
      type: 'Ui/HideFinal'
    })
  }, [dispatch])

  const submitSuccess = useCallback(() => {
    showAlert(t('ui.form.success.headline'), t('ui.form.success.subline'))
    hideFinal()
    form.current?.reset()
  }, [hideFinal, t])

  const submitError = useCallback(
    (error) => {
      showAlert(t('ui.form.submit.error'), error, undefined, true)
    },
    [t]
  )

  const onSubmit = useCallback(
    (formData: FormData) => {
      submit(formData, submitSuccess, submitError)
    },
    [submit, submitError, submitSuccess]
  )

  const extraData = useMemo<Record<string, string>>(
    () => ({
      config: JSON.stringify({
        type: configuration.type,
        application: applicationId,
        modules: [configuration.lightingHead, ...configuration.modules],
        services: configuration.services,
        properties: configuration.properties
      })
    }),
    [
      applicationId,
      configuration.lightingHead,
      configuration.modules,
      configuration.properties,
      configuration.services,
      configuration.type
    ]
  )

  const salutationOptions = useMemo(
    (): [string, string][] => [
      ['1', t('ui.form.salutation.1')],
      ['2', t('ui.form.salutation.2')]
    ],
    [t]
  )

  return (
    <Form onSubmit={onSubmit} scope="tx_ytconstela_ajax" extraData={extraData} ref={form} twoColumnLayout>
      <Select options={salutationOptions} label={t('ui.form.salutation')} name="salutation" required />
      <div className="col-xs-12" />
      <Input name="name" label={t('ui.form.name')} required />
      <Input name="firstname" label={t('ui.form.firstname')} required />
      <Input name="company" label={t('ui.form.company')} required />
      <Input name="email" label={t('ui.form.email')} type="email" required />
      <Input name="phone" label={t('ui.form.phone')} required />
      <div className="col-xs-12">
        <br />
      </div>
      <Input name="projectname" label={t('ui.form.projectname') + ' *'} required />
      <Input name="projectlocation" label={t('ui.form.projectlocation') + ' *'} required />
      <Input name="projectqty" label={t('ui.form.projectqty')} data-parsley-type="integer" data-parsley-min="1" />
      <div className="col-xs-12">
        <br />
      </div>
      <Textarea name="comment" label={t('ui.form.comment')} />
      <NoticeRequired>{t('ui.form.required')}</NoticeRequired>
      <Submit label={t('ui.form.submit')} disabled={loading} />
    </Form>
  )
}

export default FinalForm
